import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { AlertCircle, Loader2, Search, TriangleAlert, X } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useRequiresPackage } from '@/features/events/events.api';
import { PackagePicker } from '@/features/events/package-picker';
import {
  DEFAULT_OFFLINE_METHOD,
  OFFLINE_PAYMENT_METHODS,
  paymentMethodLabelKey,
} from '@/features/events/payment-methods';
import { formatCurrency, formatDateTime, formatPhone, initials } from '@/lib/format';
import { cn } from '@/lib/utils';
import { useCreateBooking, useUserSearch } from './sales.api';
import type { BookingTimeslot } from './types';

/**
 * Books a person into a timeslot on their behalf.
 *
 * The three modes are the point. "Charge normally" runs the usual payment flow;
 * "no charge" books them in with nothing collected — which is exactly what
 * appears later as Comped on the dashboard; "paid in person" records the money
 * in the same call. Naming that choice in words rather than hiding it behind a
 * bypass-payment toggle is what stops a comp being created by accident.
 */

const CHARGE = 'charge';
const NO_CHARGE = 'no_charge';
const PAID_OFFLINE = 'paid_offline';
type Mode = typeof CHARGE | typeof NO_CHARGE | typeof PAID_OFFLINE;

export function AddBookingDialog({
  timeslot,
  eventId,
  eventName,
  eventPrice,
  open,
  onOpenChange,
  onBooked,
}: {
  timeslot: BookingTimeslot | null;
  eventId: string | null;
  eventName: string | null;
  eventPrice: number | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onBooked: () => void;
}) {
  const { t } = useTranslation();
  const create = useCreateBooking();
  const userSearch = useUserSearch();
  // One active package on the event and the server refuses a booking without.
  const packageGate = useRequiresPackage(open ? eventId : null);

  const [hydrated, setHydrated] = useState<string | null>(null);
  const [userQuery, setUserQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState<{
    id: string;
    completeName: string | null;
    firstName: string | null;
    lastName: string | null;
    email: string | null;
    phoneNumber: string | null;
    avatar: string | null;
  } | null>(null);
  const [mode, setMode] = useState<Mode>(NO_CHARGE);
  const [packageId, setPackageId] = useState<string | null>(null);
  const [comments, setComments] = useState('');
  const [amount, setAmount] = useState<number | null>(null);
  const [method, setMethod] = useState(String(DEFAULT_OFFLINE_METHOD));
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Hydrate once per timeslot; the dialog stays mounted between openings.
  if (timeslot && hydrated !== timeslot.id) {
    setHydrated(timeslot.id);
    setUserQuery('');
    setSelectedUser(null);
    setMode(NO_CHARGE);
    setPackageId(null);
    setComments('');
    setError(null);
    // Pre-fill with the event's price; overwriting it is how a discounted rate
    // gets recorded.
    setAmount((eventPrice ?? 0) > 0 ? eventPrice : null);
    setMethod(String(DEFAULT_OFFLINE_METHOD));
    setReference('');
    setNotes('');
    userSearch.reset();
  }

  if (!timeslot) return null;

  const isFull = (timeslot.spotsLeft ?? 0) <= 0;
  const canSave =
    !!selectedUser &&
    // Closed until the package list has answered, so nothing slips through in
    // the window before it arrives.
    !packageGate.loading &&
    (!packageGate.required || !!packageId) &&
    (mode !== PAID_OFFLINE || (amount ?? 0) > 0);

  const modes: { value: Mode; label: string; help: string }[] = [
    { value: CHARGE, label: t('sales.modeCharge'), help: t('sales.modeChargeHelp') },
    { value: NO_CHARGE, label: t('sales.modeNoCharge'), help: t('sales.modeNoChargeHelp') },
    { value: PAID_OFFLINE, label: t('sales.modePaidOffline'), help: t('sales.modePaidOfflineHelp') },
  ];

  function book() {
    setError(null);
    create.mutate(
      {
        eventTimeSlotId: timeslot!.id,
        userId: selectedUser!.id,
        comments,
        chargeNormally: mode === CHARGE,
        // Left off entirely on an unpackaged event.
        ...(packageId ? { eventPackageId: packageId } : {}),
        // Only when money actually changed hands.
        ...(mode === PAID_OFFLINE && (amount ?? 0) > 0
          ? {
              amountReceived: amount!,
              paymentMethod: Number(method),
              offlineReference: reference,
              adminNotes: notes,
            }
          : {}),
      },
      {
        onSuccess: () => {
          toast.success(t('sales.bookingAdded'));
          onOpenChange(false);
          onBooked();
        },
        onError: (e) =>
          setError(e instanceof Error ? e.message : t('sales.actionFailed')),
      }
    );
  }

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-h-[90vh] overflow-y-auto">
        <AlertDialogHeader>
          <AlertDialogTitle>{t('sales.addBooking')}</AlertDialogTitle>
          <AlertDialogDescription>
            {eventName} · {formatDateTime(timeslot.startTime)}
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-3">
          {error && (
            <div className="border-destructive/30 bg-destructive/8 text-destructive flex items-start gap-2 rounded-xl border p-3 text-sm">
              <AlertCircle className="mt-0.5 size-4 shrink-0" />
              <p>{error}</p>
            </div>
          )}

          <div className="bg-muted rounded-xl border p-2.5 text-xs">
            {isFull ? (
              <Badge variant="destructive">{t('sales.full')}</Badge>
            ) : (
              <span>
                {t('sales.nSpotsLeft', { count: timeslot.spotsLeft ?? 0 })}
              </span>
            )}
          </div>

          {/* Overbooking is allowed on purpose — staff routinely squeeze
              somebody in — but it should be a visible decision rather than a
              surprise. */}
          {isFull && (
            <div className="border-warning/30 bg-warning/10 flex items-start gap-2 rounded-xl border p-3 text-sm">
              <TriangleAlert className="text-warning mt-0.5 size-4 shrink-0" />
              <p>{t('sales.timeslotFullWarning')}</p>
            </div>
          )}

          {/* ------------------------------------------------------ who */}
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold">{t('sales.user')}</Label>
            {selectedUser ? (
              <div className="flex items-center gap-2.5 rounded-xl border p-2.5">
                <Avatar className="size-9 shrink-0">
                  {selectedUser.avatar && (
                    <AvatarImage src={selectedUser.avatar} alt="" />
                  )}
                  <AvatarFallback>
                    {initials(selectedUser.firstName, selectedUser.lastName)}
                  </AvatarFallback>
                </Avatar>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm">{selectedUser.completeName}</p>
                  <p className="text-muted-foreground truncate text-xs">
                    {selectedUser.email}
                  </p>
                  {selectedUser.phoneNumber && (
                    <p className="text-muted-foreground truncate text-xs tabular-nums">
                      {formatPhone(selectedUser.phoneNumber)}
                    </p>
                  )}
                </div>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  aria-label={t('sales.remove')}
                  disabled={create.isPending}
                  onClick={() => setSelectedUser(null)}
                >
                  <X className="size-4" />
                </Button>
              </div>
            ) : (
              <div className="space-y-2">
                <form
                  role="search"
                  className="flex gap-2"
                  onSubmit={(event) => {
                    event.preventDefault();
                    if (userQuery.trim()) userSearch.mutate(userQuery.trim());
                  }}
                >
                  <div className="relative flex-1">
                    <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2" />
                    <Input
                      value={userQuery}
                      onChange={(e) => setUserQuery(e.target.value)}
                      placeholder={t('sales.searchUserPlaceholder')}
                      aria-label={t('sales.searchUserPlaceholder')}
                      className="pl-9"
                    />
                  </div>
                  <Button type="submit" disabled={userSearch.isPending}>
                    {userSearch.isPending ? (
                      <Loader2 className="animate-spin" />
                    ) : (
                      t('common.search')
                    )}
                  </Button>
                </form>

                {userSearch.isSuccess && userSearch.data.length === 0 && (
                  <p className="text-muted-foreground text-xs">
                    {t('sales.noUsersFound')}
                  </p>
                )}

                {(userSearch.data ?? []).length > 0 && (
                  <div className="max-h-48 divide-y overflow-y-auto rounded-xl border">
                    {userSearch.data!.map((user) => (
                      <button
                        key={user.id}
                        type="button"
                        className="hover:bg-accent flex w-full items-center gap-2.5 p-2.5 text-left transition-colors"
                        onClick={() => {
                          setSelectedUser(user);
                          userSearch.reset();
                          setUserQuery('');
                        }}
                      >
                        <Avatar className="size-8 shrink-0">
                          {user.avatar && <AvatarImage src={user.avatar} alt="" />}
                          <AvatarFallback>
                            {initials(user.firstName, user.lastName)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="min-w-0">
                          <p className="truncate text-sm">{user.completeName}</p>
                          <p className="text-muted-foreground truncate text-xs">
                            {user.email}
                          </p>
                          {user.phoneNumber && (
                            <p className="text-muted-foreground truncate text-xs tabular-nums">
                              {formatPhone(user.phoneNumber)}
                            </p>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <PackagePicker
            eventId={open ? eventId : null}
            value={packageId}
            onChange={(id, chosen) => {
              setPackageId(id);
              // The amount to collect follows what the package sells at, the
              // same way it was pre-filled from the event's own price.
              setAmount(chosen.price > 0 ? chosen.price : null);
            }}
            label={t('sales.package')}
            disabled={create.isPending}
          />

          {/* -------------------------------------------------- how paid */}
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold">
              {t('sales.howItIsPaid')}
            </Label>
            <div className="space-y-1">
              {modes.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  disabled={create.isPending}
                  onClick={() => setMode(option.value)}
                  className={cn(
                    'hover:bg-accent w-full rounded-lg border p-2.5 text-left transition-colors',
                    mode === option.value && 'border-primary bg-accent'
                  )}
                >
                  <p className="text-sm">{option.label}</p>
                  <p className="text-muted-foreground text-xs">{option.help}</p>
                </button>
              ))}
            </div>
          </div>

          {mode === PAID_OFFLINE && (
            <div className="border-primary/40 space-y-3 border-l-2 py-1 pl-3">
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold">
                    {t('sales.amount')}
                  </Label>
                  <Input
                    type="number"
                    min={0}
                    step="0.01"
                    value={amount ?? ''}
                    onChange={(e) =>
                      setAmount(
                        e.target.value === '' ? null : Number(e.target.value)
                      )
                    }
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold">
                    {t('sales.method')}
                  </Label>
                  <Select value={method} onValueChange={setMethod}>
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {OFFLINE_PAYMENT_METHODS.map((value) => (
                        <SelectItem key={value} value={String(value)}>
                          {t(paymentMethodLabelKey(value))}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold">
                  {t('sales.reference')}
                </Label>
                <Input
                  value={reference}
                  onChange={(e) => setReference(e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold">
                  {t('sales.adminNotes')}
                </Label>
                <Textarea
                  rows={2}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                />
              </div>
            </div>
          )}

          <div className="space-y-1.5">
            <Label className="text-xs font-semibold">
              {t('sales.comments')}
            </Label>
            <Textarea
              rows={2}
              value={comments}
              onChange={(e) => setComments(e.target.value)}
            />
          </div>

          {selectedUser && (
            <p className="bg-accent text-accent-foreground rounded-xl border p-3 text-sm">
              {mode === CHARGE && t('sales.echoCharge', { name: selectedUser.completeName ?? '' })}
              {mode === NO_CHARGE && t('sales.echoNoCharge', { name: selectedUser.completeName ?? '' })}
              {mode === PAID_OFFLINE &&
                t('sales.echoPaidOffline', {
                  name: selectedUser.completeName ?? '',
                  amount: formatCurrency(amount),
                  method: t(paymentMethodLabelKey(Number(method))).toLowerCase(),
                })}
            </p>
          )}
        </div>

        <div className="flex justify-end gap-2">
          <Button
            variant="outline"
            disabled={create.isPending}
            onClick={() => onOpenChange(false)}
          >
            {t('common.cancel')}
          </Button>
          <Button disabled={!canSave || create.isPending} onClick={book}>
            {create.isPending && <Loader2 className="animate-spin" />}
            {t('sales.addBooking')}
          </Button>
        </div>
      </AlertDialogContent>
    </AlertDialog>
  );
}
